import update from "immutability-helper";
import React from "react";
import { connect } from "react-redux";
import { Button, Input } from "semantic-ui-react";

import { selector } from "../.";
import { FormRow } from "../../components";
import { addGuest, updateGuest } from "../../redux/actionCreators/guestlist";
import { type ApiError } from "../model";
import BaseReactSelectDropdown from "components/dropdowns/reactselect/BaseReactSelectDropdown";

interface IProps {
  onCloseModal: () => void
}

interface IState {
  guest: server.dto.LegacyGuest
  initialPlaceId: number
  emailIsValid: boolean
  typeIsValid: boolean
  firstNameIsValid: boolean
  lastNameIsValid: boolean
  passportIsValid: boolean
  phoneIsValid: boolean
}

interface IPropsFromState {
  guestlist: server.dto.LegacyGuestlist
  errors: ApiError[]
  givenGuest: server.dto.LegacyGuest
  isLoading: boolean
}

interface IPropsFromDispatch {
  addGuest: (guest: server.dto.LegacyGuest) => any
  updateGuest: (guest: server.dto.LegacyGuest) => any
}

const mapStateToProps = (state): IPropsFromState => ({
  errors: selector.getAddingModalErrors(state),
  givenGuest: selector.getAddingGuest(state),
  guestlist: selector.getGuestlist(state),
  isLoading: selector.getAddingModalIsLoading(state)
});

const mapDispatchToProps = (dispatch): IPropsFromDispatch => ({
  addGuest: (guest: server.dto.LegacyGuest): any => dispatch(addGuest(guest, guest.guestlistPlaceId)),
  updateGuest: (guest: server.dto.LegacyGuest): any => dispatch(updateGuest(guest))
});

class GuestAdd extends React.Component<IProps & IPropsFromDispatch & IPropsFromState, IState> {
  private readonly isNewGuest = (): boolean => this.props.givenGuest == null;
  private readonly getAvailablePlaces = (): server.dto.LegacyGuestlistPlace[] => this.props.guestlist.availablePlaces;

  constructor(props: IProps & IPropsFromDispatch & IPropsFromState) {
    super(props);

    const defaultGuest: server.dto.LegacyGuest = {
      addedByUser: null,
      addedOnLocalTime: null,
      addedOnUtc: null,
      bringsGuests: 0,
      email: "",
      firstName: "",
      guestId: 0,
      guestlistPlaceId: 0,
      lastName: "",
      localFormattedTime: "",
      passport: "",
      phone: ""
    };
    this.state = {
      emailIsValid: true,
      firstNameIsValid: true,
      guest: defaultGuest,
      initialPlaceId: 0,
      lastNameIsValid: true,
      passportIsValid: true,
      phoneIsValid: true,
      typeIsValid: true
    };
  }

  componentDidMount() {
    if (this.isNewGuest()) { // New guest must have a default "place" set up
      const places = this.getAvailablePlaces();
      if (places.length !== 0) {
        const guest = Object.assign({}, this.state.guest);
        guest.guestlistPlaceId = null;
        this.setState({ guest });
      }
    } else {
      const { givenGuest } = this.props;

      const guest: server.dto.LegacyGuest = {
        ...givenGuest,
        email: givenGuest.email ?? "",
        passport: givenGuest.passport ?? "",
        phone: givenGuest.phone ?? ""
      };

      this.setState({
        guest,
        initialPlaceId: this.props.givenGuest.guestlistPlaceId
      });
    }
  }

  private readonly getEntryTypeFormat = (place: server.dto.LegacyGuestlistPlace): JSX.Element => {
    const isFull = place.emptyPlaces === 0;
    const style = isFull ? { color: "#d60500" } : {};
    const placesLeft = RESX.Guestlist.PlacesLeft.replace("{0}", place.emptyPlaces.toString());
    const placesTotal = RESX.Guestlist.PlacesTotal.replace("{0}", place.availableSpots.toString());
    return (
      <div className="flex">
        <div style={style} className="grow1">{place.entryType.name}</div>
        <div style={style}>{placesLeft} / {placesTotal}</div>
      </div>);
  };

  private readonly onEntryTypeChange = (place: server.dto.LegacyGuestlistPlace) => {
    const newState = update(this.state, {
      guest: {
        bringsGuests: { $set: 0 },
        guestlistPlaceId: { $set: place.guestlistPlaceId }
      }
    });

    this.setState(newState);
  };

  private readonly getEntryTypeSelect = (): JSX.Element => {
    return <BaseReactSelectDropdown
      options={this.getAvailablePlaces()}
      onChange={this.onEntryTypeChange}
      value={this.getAvailablePlaces().find(p => p.guestlistPlaceId === this.state.guest.guestlistPlaceId)}
      getLabel={item => item.entryType.name}
      getValue={item => item.guestlistPlaceId}
      renderOption={this.getEntryTypeFormat}
      excludeKeys={[]}
      config={{
        hasError: !this.state.typeIsValid
      }}
    />;
  };

  private readonly personOnChange = (value: number) => {
    const newState = update(this.state, {
      guest: {
        bringsGuests: { $set: value }
      }
    });

    this.setState(newState);
  };

  private readonly getPersonSelect = (): JSX.Element => {
    const { guestlistPlaceId } = this.state.guest;

    let availableExtras = 49;
    const isPlaceSelected = guestlistPlaceId != null && guestlistPlaceId > 0;
    if (isPlaceSelected) {
      const place = this.props.guestlist.availablePlaces.find((p) => p.guestlistPlaceId === guestlistPlaceId);
      availableExtras = place.emptyPlaces;

      // When editing a guest, the current "total used places" include the edited guest's info within its old type, so we need to account for that
      if (!this.isNewGuest() && place.guestlistPlaceId === this.props.givenGuest.guestlistPlaceId) {
        availableExtras += this.props.givenGuest.bringsGuests + 1;
      }
    }

    const personsOptions = Array(availableExtras).fill(0).map((_, idx) => ({
      text: `+${idx}`,
      value: idx
    }));

    return <BaseReactSelectDropdown
      options={personsOptions}
      onChange={v => { this.personOnChange(v.value); }}
      value={personsOptions.find(p => p.value === this.state.guest.bringsGuests)}
      getLabel={item => item.text}
      getValue={item => item.value}
      excludeKeys={[]}
      config={{
        hasError: !this.state.typeIsValid
      }}
    />;
  };

  private readonly onChange = (event) => {
    const guest = { ...this.state.guest };
    guest[event.target.name] = event.target.value;
    this.setState({ guest });
  };

  private readonly onSubmit = () => {
    if (!this.validateAll()) {
      return;
    }

    let promise = null;
    if (this.isNewGuest()) {
      promise = this.props.addGuest(this.state.guest);
    } else {
      promise = this.props.updateGuest(this.state.guest);
    }

    promise.then(() => {
      if (!this.hasErrors()) {
        this.props.onCloseModal();
      }
    });
  };

  private readonly hasErrors = (): boolean => {
    const { errors } = this.props;
    return errors.length !== 0;
  };

  private readonly isValidEmail = (): boolean => {
    // If email is not mandatory, then it can be left empty. However, if it's filled in, it must be validated for correctness, even if email isn't mandatory
    const emailIsEmpty = this.state.guest.email.trim() === "";
    const emailIsMandatory = this.props.guestlist.emailMandatory;
    if (emailIsMandatory && emailIsEmpty) {
      return false;
    } else if (!emailIsMandatory && emailIsEmpty) {
      return true;
    }

    const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(this.state.guest.email);
  };

  private readonly validateEmail = () => {
    this.setState({ emailIsValid: this.isValidEmail() });
  };

  private readonly validateType = () => {
    this.setState({ typeIsValid: this.isValidType() });
  };

  private readonly validateFirstName = () => {
    this.setState({ firstNameIsValid: this.isValidFirstName() });
  };

  private readonly validateLastName = () => {
    this.setState({ lastNameIsValid: this.isValidLastName() });
  };

  private readonly validatePhone = () => {
    this.setState({ phoneIsValid: this.isValidPhone() });
  };

  private readonly isValidType = () => {
    const selectedPlace = this.props.guestlist.availablePlaces.find((p) => p.guestlistPlaceId === this.state.guest.guestlistPlaceId);

    // When editing guests, the calculation if the current type is full or not is distorted, because the edited guest info is already part of the "usedPlaces"
    // Keeping your own type is always allowed, so the calculation can be disregarded
    if (!this.isNewGuest() && selectedPlace != null && selectedPlace.guestlistPlaceId === this.props.givenGuest.guestlistPlaceId) {
      return true;
    }

    const placeIsFull = selectedPlace != null && (selectedPlace.availableSpots === selectedPlace.usedPlaces);
    return !placeIsFull && selectedPlace != null;
  };

  private readonly isValidFirstName = () => this.state.guest.firstName.trim() !== "";

  private readonly isValidLastName = () => this.state.guest.lastName.trim() !== "";

  private readonly isValidPhone = (): boolean => {
    const phoneIsEmpty = this.state.guest.phone.trim() === "";
    const phoneIsMandatory = this.props.guestlist.phoneMandatory;

    return !(phoneIsMandatory && phoneIsEmpty);
  };

  private readonly isValidPassport = (): boolean => {
    const passportIsEmpty = this.state.guest.passport.trim() === "";
    return !(this.props.guestlist.passportRequired && passportIsEmpty);
  };

  private readonly validateAll = (): boolean => {
    const firstNameIsValid = this.isValidFirstName();
    const lastNameIsValid = this.isValidLastName();
    const emailIsValid = this.isValidEmail();
    const phoneIsValid = this.isValidPhone();
    const passportIsValid = this.isValidPassport();
    const typeIsValid = this.isValidType();

    if (!(firstNameIsValid && lastNameIsValid && emailIsValid && phoneIsValid && passportIsValid && typeIsValid)) {
      this.setState({
        emailIsValid,
        firstNameIsValid,
        lastNameIsValid,
        passportIsValid,
        phoneIsValid,
        typeIsValid
      });
      return false;
    }

    return true;
  };

  public render(): JSX.Element {
    if (this.state == null) {
      return null;
    }

    const { guest, emailIsValid, firstNameIsValid, lastNameIsValid, phoneIsValid, passportIsValid } = this.state;

    return (<>
      <div className="flex-column gap">
        <div className="flex-column gap">
          <FormRow label={RESX.GeneralLabels.Name}>
            <div className="flex">
              <div className="grow1 margin-right">
                <Input
                  name="firstName"
                  error={!firstNameIsValid}
                  onChange={this.onChange}
                  onBlur={this.validateFirstName}
                  value={guest.firstName}
                  maxLength={serverReference.constants.personName.maxFirstNameLength}
                  placeholder={RESX.Contact.FirstName}
                  fluid />
              </div>
              <div className="grow1">
                <Input
                  name="lastName"
                  error={!lastNameIsValid}
                  onChange={this.onChange}
                  onBlur={this.validateLastName}
                  value={guest.lastName}
                  maxLength={serverReference.constants.personName.maxLastNameLength}
                  placeholder={RESX.Contact.LastName}
                  fluid />
              </div>
            </div>
          </FormRow>
          <FormRow label={RESX.GeneralLabels.Email}>
            <Input
              name="email"
              error={!emailIsValid}
              onChange={this.onChange}
              onBlur={this.validateEmail}
              value={guest.email}
              maxLength={serverReference.constants.mailAddress.maxEmailLength}
              fluid />
          </FormRow>
          <FormRow label={RESX.Contact.Mobile}>
            <Input name="phone" error={!phoneIsValid} onChange={this.onChange} onBlur={this.validatePhone} value={guest.phone} maxLength={50} fluid />
          </FormRow>
          {this.props.guestlist.passportRequired && (
            <FormRow label={RESX.Guestlist.Passport}>
              <Input name="passport" error={!passportIsValid} onChange={this.onChange} onBlur={this.validatePhone} value={guest.passport} maxLength={15} fluid />
            </FormRow>
          )}
          <FormRow label={RESX.Guestlist.List}>
            <div>
              {this.getEntryTypeSelect()}
            </div>
          </FormRow>
          {this.props.guestlist.multipleGuestsPerName && (
            <FormRow label={RESX.Guestlist.BringsGuestsLbl}>
              <div>
                {this.getPersonSelect()}
              </div>
            </FormRow>
          )}
        </div>
      </div>

      <div className="flex">
        <div className="grow1">{this.props.errors.map((error) => <div key={error.code} style={{ color: "red" }}>{error.message}</div>)}</div>
        <div>
          <Button
            onClick={this.onSubmit}
            disabled={this.props.isLoading}
            color="green">{RESX.Buttons.btnSave}</Button>
        </div>
      </div>
    </>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(GuestAdd);
